import React from "react";
import { Button, Table, Tag, Drawer } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { NAVY, FORMAT_COLORS, RED } from "../gateway.constants";

/**
 * FieldPreviewDrawer — slide-out showing all fields for a template
 * plus a sample output snippet.
 */
function FieldPreviewDrawer({ open, template, fields, onClose, onUse }) {
  if (!template) return null;
  const fmt = FORMAT_COLORS[template.formatTypeId] || { bg: "#f5f5f5", text: "#666" };

  const sampleRow = {};
  fields.forEach((f) => {
    if      (f.dataType === "DATE")                     sampleRow[f.fieldName] = "2026-01-15";
    else if (f.dataType === "NUMERIC")                  sampleRow[f.fieldName] = "1250.000000";
    else if (f.dataType === "INTEGER")                  sampleRow[f.fieldName] = "1000";
    else if (f.dataType === "ENUM" && f.allowedValues)  sampleRow[f.fieldName] = f.allowedValues.split(",")[0];
    else if (f.defaultValue)                            sampleRow[f.fieldName] = f.defaultValue;
    else                                                sampleRow[f.fieldName] = `[${f.fieldName}]`;
  });

  const sampleOutput = () => {
    const d = template.delimiter || ",";
    const slice = fields.slice(0, 8);
    if (template.formatTypeId === "XML_NUGGET") {
      const lines = slice.map((f) => {
        const el = (f.xmlPath || f.fieldName).split("/").pop();
        return `    <${el}>${sampleRow[f.fieldName]}</${el}>`;
      });
      return `<${template.dataType}>\n${lines.join("\n")}\n    ...\n</${template.dataType}>`;
    }
    if (template.formatTypeId === "IDX_RECORD") {
      return `TR${d}${slice.map((f) => sampleRow[f.fieldName]).join(d)}`;
    }
    const hdr = slice.map((f) => f.fieldName).join(d);
    const row = slice.map((f) => sampleRow[f.fieldName]).join(d);
    return `${hdr}\n${row}`;
  };

  const cols = [
    {
      title: "#", dataIndex: "seqNo", key: "seqNo", width: 36,
      render: (n) => <span style={{ color: "#bbb", fontSize: 11 }}>{n}</span>,
    },
    {
      title: "Field", dataIndex: "fieldName", key: "fieldName",
      render: (t, r) => (
        <div>
          <div style={{ fontSize: 12, fontWeight: 500 }}>{t}</div>
          <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>{r.displayName}</div>
        </div>
      ),
    },
    {
      title: "Type", dataIndex: "dataType", key: "dataType", width: 80,
      render: (t) => <Tag style={{ fontSize: 10 }}>{t}</Tag>,
    },
    {
      title: "Format/Length", key: "fmt", width: 100,
      render: (_, r) => (
        <span style={{ fontSize: 11, color: "#888" }}>
          {r.dateFormat || (r.maxLength ? `max ${r.maxLength}` : r.decimalPlaces ? `${r.decimalPlaces}dp` : "—")}
        </span>
      ),
    },
    {
      title: "Req", dataIndex: "isMandatory", key: "req", width: 40,
      render: (v) => v
        ? <span style={{ color: RED, fontSize: 13 }}>✱</span>
        : <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Default", dataIndex: "defaultValue", key: "def", width: 80,
      render: (t) => t ? <code style={{ fontSize: 10 }}>{t}</code> : "—",
    },
  ];

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 600 }}>{template.templateName}</span>
          <span style={{
            fontSize: 11, padding: "2px 8px", borderRadius: 10,
            background: fmt.bg, color: fmt.text, fontWeight: 600,
          }}>
            {template.formatTypeId?.replace("_", " ")}
          </span>
        </div>
      }
      width={680}
      open={open}
      onClose={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" style={{ background: NAVY }} onClick={() => { onUse(template); onClose(); }}>
            Use this format →
          </Button>
        </div>
      }
    >
      {/* Meta row */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "Data type",  value: template.dataType },
          { label: "Delimiter",  value: template.delimiter || (template.formatTypeId === "XML_NUGGET" ? "N/A" : ",") },
          { label: "Header",     value: template.hasHeader ? "Yes" : "No" },
          { label: "Fields",     value: fields.length },
          { label: "Mandatory",  value: fields.filter((f) => f.isMandatory).length },
          { label: "Output file", value: template.outputFilenamePattern || "auto" },
        ].map(({ label, value }) => (
          <div key={label}>
            <div style={{
              fontSize: 10, color: "var(--color-text-secondary)",
              textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2,
            }}>
              {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Sample output */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 11, fontWeight: 600, marginBottom: 8,
          color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: ".5px",
        }}>
          Sample output (first 8 fields)
        </div>
        <pre style={{
          background: NAVY, color: "#93C5FD", fontSize: 11,
          padding: 14, borderRadius: 8, overflowX: "auto",
          lineHeight: 1.7, margin: 0, fontFamily: "monospace",
        }}>
          {sampleOutput()}
        </pre>
      </div>

      <div style={{
        fontSize: 11, fontWeight: 600, marginBottom: 8,
        color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: ".5px",
      }}>
        All fields ({fields.length})
      </div>
      <Table
        dataSource={fields} columns={cols} rowKey="fieldId"
        size="small" pagination={false} scroll={{ y: 320 }}
      />
    </Drawer>
  );
}

export { FieldPreviewDrawer };
export default FieldPreviewDrawer;
